import { useState } from 'react';
import { api } from '../lib/api';
import { useMe } from '../lib/me';
import { fmtAgo, fmtBytes, fmtDate, useApi } from '../lib/hooks';
import { Icon } from '../components/Icon';
import { GoogleDocsPanel } from './GoogleDocs';
import { Alert, Badge, Button, ErrorBox, Loading, Modal, Panel, Toggle, useAction } from '../components/ui';

interface Dataset {
  id: string; name: string; description: string; urlTemplate: string; enabled: boolean; version: string | null; url: string | null;
  sizeBytes: number; records: number | null; loadedAt: string | null; status: 'ok' | 'error' | 'missing'; error: string | null;
  lastAttemptAt: string | null; usedBy: { id: string; name: string }[]; upToDate: boolean;
}
interface Source {
  id: string; name: string; description: string; homepage: string; versionUrl: string; channel: string; kind: 'versioned' | 'page';
  autoUpdate: boolean; checkIntervalMinutes: number; latestVersion: string | null; lastCheckedAt: string | null;
  lastCheckResult: 'ok' | 'error' | null; lastCheckError: string | null; lastUpdatedAt: string | null; datasets: Dataset[];
}

const INTERVALS = [[15, '15 minutes'], [30, '30 minutes'], [60, '1 hour'], [180, '3 hours'], [360, '6 hours'], [720, '12 hours'], [1440, '24 hours']] as const;

function DatasetStatus({ d, latest }: { d: Dataset; latest: string | null }) {
  if (!d.enabled) return <Badge>off</Badge>;
  if (d.status === 'missing' && !d.version) return <Badge tone="amber">not loaded</Badge>;
  if (d.upToDate) return <Badge tone="green"><Icon name="check" size={11} /> up to date</Badge>;
  if (d.version && latest && d.version !== latest) return <Badge tone="amber" title={`Loaded ${d.version}, latest ${latest}`}>outdated</Badge>;
  return <Badge tone="red" title={d.error ?? ''}>error</Badge>;
}

export function DataSourcesPage() {
  const { can } = useMe();
  const manage = can('admin.datasources.manage');
  const { data, error, loading, reload } = useApi<{ sources: Source[]; mode: 'live' | 'fake'; job: { lastRunAt?: string; lastResult?: string } | null }>('/api/admin/datasources');
  const { busy, run } = useAction();
  const [preview, setPreview] = useState<{ title: string; data: any } | null>(null);

  if (loading && !data) return <Loading />;
  if (!data) return <ErrorBox error={error} retry={reload} />;

  return (
    <div className="stack">
      <p className="muted">External datasets loaded into the dashboard for tools to use. The dashboard checks each source on a schedule and, with auto-update on, downloads new versions as soon as a new game build is published.</p>
      {data.mode === 'fake' && <Alert kind="warn">Sample data mode: this server uses built-in sample files instead of downloading (set <code>DATASOURCE_MODE=live</code> to download real data).</Alert>}
      {data.sources.map((s) => {
        const outdated = s.datasets.filter((d) => d.enabled && !d.upToDate).length;
        return (
          <Panel key={s.id} icon="database" title={s.name} actions={manage && (
            <>
              <Button icon="refresh" busy={busy === `check-${s.id}`} onClick={() => run(`check-${s.id}`, async () => {
                const r = await api.post(`/api/admin/datasources/${s.id}/check`, {});
                await reload();
                return r;
              }, 'Version checked')}>Check now</Button>
              <Button variant="primary" icon="download" busy={busy === `refresh-${s.id}`} disabled={!s.latestVersion && s.kind !== 'page'}
                onClick={() => run(`refresh-${s.id}`, async () => { await api.post(`/api/admin/datasources/${s.id}/refresh`, {}); await reload(); }, 'Datasets downloaded')}>
                Download all
              </Button>
            </>
          )}>
            <p className="muted" style={{ marginTop: 0 }}>{s.description} <a href={s.homepage} target="_blank" rel="noreferrer noopener">{s.homepage.replace(/^https?:\/\//, '')} <Icon name="external" size={12} /></a></p>
            <div className="facts ds-facts">
              <div><span className="field-label">{s.kind === 'page' ? 'Content version' : `Latest ${s.channel} build`}</span><strong className="mono ds-version">{s.latestVersion ?? 'unknown'}</strong></div>
              <div><span className="field-label">Last checked</span>
                <strong title={fmtDate(s.lastCheckedAt)}>{fmtAgo(s.lastCheckedAt)}</strong>
                {s.lastCheckResult === 'error' && <span className="bad-text small"> {s.lastCheckError}</span>}
              </div>
              <div><span className="field-label">Status</span>
                {outdated ? <strong className="warn-text">{outdated} dataset{outdated === 1 ? '' : 's'} need updating</strong> : <strong className="ok-text">All enabled datasets current</strong>}
              </div>
              <div><span className="field-label">Auto-update</span>
                <div className="ds-auto">
                  <Toggle checked={s.autoUpdate} disabled={!manage} label="Auto-update" onChange={(v) => run('auto', async () => { await api.patch(`/api/admin/datasources/${s.id}`, { autoUpdate: v }); await reload(); })} />
                  <select value={s.checkIntervalMinutes} disabled={!manage} aria-label="Check interval"
                    onChange={(e) => run('int', async () => { await api.patch(`/api/admin/datasources/${s.id}`, { checkIntervalMinutes: Number(e.target.value) }); await reload(); })}>
                    {INTERVALS.map(([v, l]) => <option key={v} value={v}>every {l}</option>)}
                    {!INTERVALS.some(([v]) => v === s.checkIntervalMinutes) && <option value={s.checkIntervalMinutes}>every {s.checkIntervalMinutes} min</option>}
                  </select>
                </div>
              </div>
            </div>
            <div className="table-wrap" style={{ marginTop: 16 }}>
              <table className="table ds-table">
                <thead><tr><th>Dataset</th><th>Loaded version</th><th>Status</th><th className="right">Records</th><th className="right">Size</th><th>On</th><th /></tr></thead>
                <tbody>
                  {s.datasets.map((d) => (
                    <tr key={d.id}>
                      <td>
                        <strong>{d.name}</strong>{d.usedBy.map((t) => <Badge key={t.id} tone="blue">{t.name}</Badge>)}
                        <div className="muted small">{d.description}</div>
                        {d.error && <div className="bad-text small">Last attempt failed: {d.error}</div>}
                      </td>
                      <td className="nowrap"><span className="mono">{d.version ?? '—'}</span>{d.loadedAt && <div className="muted small" title={fmtDate(d.loadedAt)}>loaded {fmtAgo(d.loadedAt)}</div>}</td>
                      <td><DatasetStatus d={d} latest={s.latestVersion} /></td>
                      <td className="right">{d.records?.toLocaleString() ?? '—'}</td>
                      <td className="right nowrap">{d.sizeBytes ? fmtBytes(d.sizeBytes) : '—'}</td>
                      <td><Toggle checked={d.enabled} disabled={!manage} label={`Enable ${d.name}`}
                        onChange={(v) => run(`en-${d.id}`, async () => { await api.patch(`/api/admin/datasources/${s.id}/datasets/${d.id.split('.').slice(1).join('.')}`, { enabled: v }); await reload(); })} /></td>
                      <td>
                        <div className="row-actions">
                          {d.url && <a className="icon-btn" href={d.url} target="_blank" rel="noreferrer noopener" title="Open source file"><Icon name="external" size={15} /></a>}
                          {d.version && <Button variant="ghost" icon="eye" aria-label="Preview" title="Preview structure"
                            onClick={() => run(`pv-${d.id}`, async () => setPreview({ title: d.name, data: await api.get(`/api/admin/datasources/${s.id}/datasets/${d.id.split('.').slice(1).join('.')}/preview`) }))} />}
                          {manage && <Button variant="ghost" icon="download" aria-label={`Download ${d.name}`} title="Download this dataset now" busy={busy === `dl-${d.id}`} disabled={!s.latestVersion && s.kind !== 'page'}
                            onClick={() => run(`dl-${d.id}`, async () => {
                              const r = await api.post(`/api/admin/datasources/${s.id}/refresh`, { datasets: [d.id.split('.').slice(1).join('.')] });
                              await reload();
                              if (r.results?.some((x: { ok: boolean }) => !x.ok)) throw new Error(r.results[0].error ?? 'Download failed');
                            }, `${d.name} downloaded`)} />}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="muted small" style={{ marginBottom: 0 }}>
              {s.kind === 'page' ? 'Read from' : 'Version source:'} <span className="mono">{s.versionUrl}</span>. Last download: {fmtDate(s.lastUpdatedAt)}.
              {data.job?.lastRunAt && <> Scheduled check last ran {fmtAgo(data.job.lastRunAt)}{data.job.lastResult && data.job.lastResult !== 'ok' ? ` (${data.job.lastResult})` : ''}.</>}
            </p>
          </Panel>
        );
      })}
      <GoogleDocsPanel manage={manage} />
      {preview && (
        <Modal title={`${preview.title} · ${preview.data.version}`} wide onClose={() => setPreview(null)}>
          <p className="muted">{preview.data.type === 'array' ? 'A list' : 'An object'} with {preview.data.records?.toLocaleString() ?? '?'} records.
            {preview.data.topLevelKeys?.length > 0 && <> Top-level keys: <span className="mono">{preview.data.topLevelKeys.join(', ')}</span></>}</p>
          <div className="field-label">Sample record</div>
          <pre className="code-block">{preview.data.sample}</pre>
        </Modal>
      )}
    </div>
  );
}
